import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, X, MapPin, Heart, MessageSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveFileUrl } from "@/components/FileDropzone";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function MatchModal({ user, onClose }) {
  if (!user) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div
        data-testid="match-modal"
        className="bg-[#FDFBF7] border-2 border-black max-w-md w-full p-8 text-center"
      >
        <div className="text-[#F7931A]">
          <Flame className="w-16 h-16 mx-auto stroke-[2]" />
        </div>
        <div className="font-display font-black uppercase text-4xl tracking-tighter mt-4">
          It&apos;s a match.
        </div>
        <div className="mt-3 font-mono text-sm uppercase tracking-widest text-[#0A0A0A]/70">
          You & {user.name}
        </div>
        <Link
          to={`/messages?to=${user.id}`}
          data-testid="match-message"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal"
        >
          <MessageSquare className="w-4 h-4" />
          Send a message
        </Link>
        <button
          onClick={onClose}
          data-testid="match-close"
          className="mt-3 w-full py-3 bg-white border-2 border-black font-bold uppercase tracking-widest btn-brutal"
        >
          Keep swiping
        </button>
      </div>
    </div>
  );
}

export default function BitDate() {
  const { user, refreshUser } = useAuth();
  const [deck, setDeck] = useState([]);
  const [i, setI] = useState(0);
  const [matched, setMatched] = useState(null);
  const [needsLocation, setNeedsLocation] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/bitdate/deck");
      setDeck(data);
      setI(0);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setNeedsLocation(!user?.lat || !user?.lng);
  }, [user]);

  const enableLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.put("/profile", { lat: pos.coords.latitude, lng: pos.coords.longitude });
          await refreshUser();
          toast.success("Location saved.");
          load();
        } catch (err) {
          toast.error(formatApiError(err));
        }
      },
      () => toast.error("Location permission denied.")
    );
  };

  const swipe = async (direction) => {
    const target = deck[i];
    if (!target) return;
    setI(i + 1);
    try {
      const { data } = await api.post("/bitdate/swipe", {
        target_user_id: target.id,
        direction,
      });
      if (data.matched) setMatched(target);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const current = deck[i];

  return (
    <main className="max-w-md mx-auto px-4 py-10">
      <div className="flex items-center gap-3 mb-6">
        <Flame className="w-6 h-6 text-[#F7931A]" />
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">BitDate</h1>
      </div>

      {needsLocation && (
        <div
          data-testid="location-banner"
          className="mb-6 border-2 border-black bg-white p-4 flex items-center justify-between gap-4"
        >
          <div className="text-sm">
            <div className="font-bold uppercase tracking-widest text-xs">Enable location</div>
            <div className="text-[#0A0A0A]/70 mt-1">Show distances to nearby matches.</div>
          </div>
          <button
            onClick={enableLocation}
            data-testid="btn-enable-location"
            className="px-3 py-2 bg-[#F7931A] border-2 border-black text-xs font-bold uppercase tracking-widest btn-brutal"
          >
            Allow
          </button>
        </div>
      )}

      {!current && (
        <div
          data-testid="empty-deck"
          className="border-2 border-black bg-white p-12 text-center"
        >
          <div className="font-display font-black text-2xl uppercase">Deck empty.</div>
          <div className="text-sm text-[#0A0A0A]/60 mt-2 font-mono uppercase tracking-widest">
            Check back later.
          </div>
          <button
            onClick={load}
            data-testid="btn-reload-deck"
            className="mt-6 px-5 py-2 bg-white border-2 border-black text-xs font-bold uppercase tracking-widest btn-brutal"
          >
            Refresh
          </button>
        </div>
      )}

      {current && (
        <>
          <div
            data-testid="swipe-card"
            className="border-2 border-black bg-[#FDFBF7] overflow-hidden"
          >
            <div className="relative w-full h-[60vh] bg-[#0A0A0A]">
              {current.avatar_url ? (
                <img src={resolveFileUrl(current.avatar_url)} alt="" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
              ) : (
                <div className="w-full h-full flex items-center justify-center font-display font-black text-8xl text-[#F7931A]">
                  {current.name?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="font-display font-black text-3xl uppercase tracking-tighter">
                {current.name}
              </div>
              <div className="mt-1 font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 flex items-center gap-2">
                <MapPin className="w-3 h-3" />
                {current.distance_km != null
                  ? `${current.distance_km} km away`
                  : current.city || "Location unknown"}
              </div>
              {current.bio && (
                <p className="mt-3 text-sm leading-relaxed">{current.bio}</p>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <button
              onClick={() => swipe("pass")}
              data-testid="btn-pass"
              className="py-4 bg-[#0A0A0A] text-[#FDFBF7] border-2 border-black font-bold uppercase tracking-widest btn-brutal flex items-center justify-center gap-2"
            >
              <X className="w-5 h-5" />
              Pass
            </button>
            <button
              onClick={() => swipe("like")}
              data-testid="btn-like"
              className="py-4 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal flex items-center justify-center gap-2"
            >
              <Heart className="w-5 h-5" />
              Like
            </button>
          </div>
        </>
      )}

      <MatchModal user={matched} onClose={() => setMatched(null)} />
    </main>
  );
}
